import { styled } from '@linaria/react';
import { Divider, Group } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import type { SessionPullRequest } from '../../../../api/agent-miner-client/agent-miner-client.type';
import { SmallSquareButton } from '../../../../components/small-square-button/small-square-button';
import { inkDarkest, inkLight, secondaryBase, skyLight, skyLighter } from '../../../../styles/design-tokens';
import { Select } from '../../../../ui-library/select/select';
import { Text } from '../../../../ui-library/typography/typography';
import { AgentSourceIcon } from '../../shared/agent-source-icon';
import { fmtNum } from '../../shared/formatters';
import { Mono, SubtleText, ThisBadge } from './pr-detail.helpers';
import { PrFileTree } from './pr-file-tree';
import { PrView } from './pull-requests-tab.type';

type PrSidePanelProps = {
  pullRequests: SessionPullRequest[];
  activeIndex: number;
  onSelectPr: (index: number) => void;
  view: PrView;
  selectedFileId: string | null;
  onSelectFile: (fileId: string) => void;
  onClose: () => void;
};

/**
 * Left-hand context panel for the Pull Requests tab (rendered in the page
 * layout's side slot): a pull-request selector, then whatever the active view
 * needs beside it: the file tree for Code, the contributing sessions for
 * Contributors, and a hint for Lineage.
 *
 * @param {PrSidePanelProps} props - PRs, selection, active view, and callbacks
 * @returns {JSX.Element} The side panel content
 */
export function PrSidePanel({
  pullRequests,
  activeIndex,
  onSelectPr,
  view,
  selectedFileId,
  onSelectFile,
  onClose,
}: PrSidePanelProps) {
  const { t } = useTranslation();
  const pr = pullRequests[Math.min(activeIndex, Math.max(0, pullRequests.length - 1))];
  const options = pullRequests.map((pullRequest) => ({
    value: pullRequest.pr_id,
    label: `${pullRequest.number != null ? `#${pullRequest.number}` : 'PR'} ${pullRequest.title ?? ''}`.trim(),
  }));

  return (
    <Panel>
      <Group justify="space-between" align="center">
        <Text weight="medium">{t('agent_miner.pull_requests.panel_title')}</Text>
        <SmallSquareButton
          onClick={onClose}
          iconName="arrow_back_ios"
          aria-label={t('agent_miner.pull_requests.panel.close')}
        />
      </Group>
      <Select
        data-bf="pr-selector"
        size="md"
        value={pr?.pr_id ?? null}
        options={options}
        enableTooltip
        onChange={(value) => {
          const index = pullRequests.findIndex((pullRequest) => pullRequest.pr_id === value);
          if (index >= 0) onSelectPr(index);
        }}
      />
      <Divider />
      {pr && view === PrView.Code && (
        // Key by PR id so switching PRs remounts the tree with a fresh collapsed
        // set — otherwise a directory collapsed on one PR stays hidden on the next.
        <PrFileTree key={pr.pr_id} files={pr.files ?? []} selectedFileId={selectedFileId} onSelect={onSelectFile} />
      )}
      {pr && view === PrView.Contributors && (
        <SessionList>
          <SectionLabel>
            {t('agent_miner.pull_requests.contributing_sessions')} ({(pr.contributing_sessions ?? []).length})
          </SectionLabel>
          {(pr.contributing_sessions ?? []).map((session) => {
            const clickable = !session.is_current;
            return (
              <SessionRow
                key={session.session_id}
                as={clickable ? 'a' : 'div'}
                $current={session.is_current}
                href={clickable ? `/application/agent-miner/sessions/${session.session_id}` : undefined}
                target={clickable ? '_blank' : undefined}
                rel={clickable ? 'noopener noreferrer' : undefined}
              >
                <SessionTop>
                  <AgentSourceIcon source={session.source} size={14} />
                  <SessionTitle title={session.title ?? ''}>{session.title}</SessionTitle>
                  {session.is_current && <ThisBadge>{t('agent_miner.pull_requests.this_session')}</ThisBadge>}
                </SessionTop>
                <SessionMeta>
                  <span>{session.user ?? '—'}</span>
                  <Mono>{t('agent_miner.pull_requests.panel.session_lines', { count: fmtNum(session.lines) })}</Mono>
                </SessionMeta>
              </SessionRow>
            );
          })}
        </SessionList>
      )}
      {pr && view === PrView.Lineage && <SubtleText>{t('agent_miner.pull_requests.panel.lineage_hint')}</SubtleText>}
    </Panel>
  );
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const SectionLabel = styled.span`
  font: 600 10.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const SessionList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SessionRow = styled.a<{ $current: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding: 8px 10px;
  border-radius: 8px;
  background: ${skyLighter};
  border: 1px solid ${(props) => (props.$current ? secondaryBase : skyLight)};
  text-decoration: none;
  min-width: 0;

  &:hover {
    border-color: ${secondaryBase};
  }
`;

const SessionTop = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

const SessionTitle = styled.span`
  font: 600 12px/1.3 'Figtree', sans-serif;
  color: ${inkDarkest};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SessionMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  font: 500 11px/1 'Figtree', sans-serif;
  color: ${inkLight};
`;
